/**
 * The contract between server and client.
 *
 * Both packages import from here. Nothing in this file may import from
 * `server/` or `client/` — it is the shared vocabulary, not a layer.
 *
 * Craft Together maintained two hand-written type mirrors that drifted apart;
 * this package exists so that cannot happen here.
 */

import type { SessionStudyBlock, StudyClientEvents, StudyServerEvents } from './study'
import type { DataQualityCode, RoundSummary, SessionDataQuality } from './quality'

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

/** Server tick period. 50 ticks per second. */
export const TICK_MS = 20
export const TICKS_PER_SECOND = 1000 / TICK_MS

/** Full-state keyframe cadence. */
export const KEYFRAME_INTERVAL_TICKS = 100 // every 2 s

export const msToTicks = (ms: number): number => Math.round(ms / TICK_MS)
export const ticksToMs = (ticks: number): number => ticks * TICK_MS

// ---------------------------------------------------------------------------
// Identity
//
// Never `A`/`B`. Player count is a room parameter so a third seat is a config
// change, not a migration.
// ---------------------------------------------------------------------------

export type PlayerId = string

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

export interface Cell {
  x: number
  y: number
}

export type Direction = 'up' | 'down' | 'left' | 'right'

export const DIRECTION_VECTORS: Record<Direction, Cell> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
}

export const cellKey = (c: Cell): string => `${c.x},${c.y}`
export const cellEquals = (a: Cell, b: Cell): boolean => a.x === b.x && a.y === b.y
export const cellAhead = (from: Cell, facing: Direction): Cell => {
  const v = DIRECTION_VECTORS[facing]
  return { x: from.x + v.x, y: from.y + v.y }
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

/** Ingredient identifiers are data-driven; these are the v1 set. */
export type IngredientId = string

/**
 * The stable id of a specific pan, a specific ticket, or a specific tile.
 *
 * Aliases rather than new types, because every one of them is already a string
 * somewhere in this file (`Container.id`, `Order.id`, `cellKey`). They exist so
 * a macro-action can *name* the thing it means — `{ verb: 'cook', container }`
 * reads as a sentence about the kitchen rather than three anonymous strings —
 * and so a reader of `Macro` below never has to go looking for what a field is.
 */
export type ContainerId = string
export type OrderId = string
/** `${x},${y}` — the output of `cellKey`, and how a station is addressed. */
export type CellKey = string
/**
 * A carried thing, named the way somebody talking about it would name it: an
 * `IngredientId`, a `ContainerId`, `plate`, or `extinguisher`.
 *
 * Deliberately not `Carryable`. A `Carryable` is the *state* of a held thing —
 * a plate carries its own id, its cleanliness and its contents — and a planner
 * asked to write one out would be asked to restate the world back at us, at
 * length, on a channel where every token is paid for. `handoff` needs a name,
 * and the executor reads the state for the rest.
 */
export type CarriedName = string

export interface Plate {
  /** Stable id so a specific plate can be followed through the wash cycle. */
  id: string
  clean: boolean
  contents: IngredientId[]
}

/**
 * A carryable container — a pan. A first-class movable object rather than a
 * fixture welded to a stove, because the kitchen's order of inclusion is
 * `ingredients -> containers -> static equipment`, and that middle arrow only
 * means anything if containers can move.
 *
 * Which kinds exist, what they hold and which equipment they sit on is
 * `recipes.json`, not code.
 */
export type ContainerKind = string

/**
 * Cooking is stored as two tick stamps rather than an accumulating counter.
 *
 * A counter would have to be incremented on every one of the 600 ticks a cook
 * takes, and every one of those increments would be a state change the event
 * log had to carry for a replay to match. Stamps mean the container's state
 * changes exactly twice per phase, and both the renderer and the replay derive
 * the bar position from the current tick.
 */
export interface Container {
  /** Stable id so a specific pan can be followed as it moves around. */
  id: string
  kind: ContainerKind
  contents: IngredientId[]
  cookState: CookState
  /** Tick the current phase began. `null` when empty or off the heat. */
  phaseStartedAtTick: number | null
  /** Tick the current phase completes. `null` when empty or off the heat. */
  phaseEndsAtTick: number | null
}

/**
 * Carried equipment — the design doc's fourth entity class, which "changes the
 * meaning of [interaction]". The fire extinguisher is the only one in v1: with
 * it in hand the single interact key puts fires out, and without it a burning
 * station refuses every interaction there is.
 */
export type EquipmentId = string

/** What a player is carrying, or what is resting on a counter. */
export type Carryable =
  | { type: 'ingredient'; id: IngredientId }
  | { type: 'plate'; plate: Plate }
  | { type: 'container'; container: Container }
  | { type: 'equipment'; id: EquipmentId }

// ---------------------------------------------------------------------------
// Stations
// ---------------------------------------------------------------------------

/**
 * Every station type, as a value rather than only as a type.
 *
 * The union is derived from this list rather than the other way round, so the
 * two can never disagree. A runtime list is what lets a surface *enumerate*
 * stations — the client's badge table is `Record<StationType, …>` and its test
 * walks this array to prove the table has no holes, which is the difference
 * between a missing station pictogram being a compile error and it being a
 * rounded square nobody notices.
 */
export const STATION_TYPES = [
  'wall',
  'floor',
  'counter',
  'board',
  'stove',
  /** The second cooking equipment. Same machinery, different recipes. */
  'oven',
  'crate',
  'plate_stack',
  'sink',
  'bin',
  'hatch',
  'plate_return',
  /** Where the fire extinguisher lives when nobody is carrying it. */
  'extinguisher_rack',
] as const

export type StationType = (typeof STATION_TYPES)[number]

/** Equipment that heats whatever container is standing on it. */
export const COOKING_EQUIPMENT: ReadonlySet<StationType> = new Set<StationType>(['stove', 'oven'])

export const isCookingEquipment = (type: StationType): boolean => COOKING_EQUIPMENT.has(type)

/** Cooking progression: cooking, then done, then burnt. */
export type CookState = 'empty' | 'cooking' | 'done' | 'burnt'

export interface Station {
  type: StationType
  cell: Cell
  /** Counters, boards and the plate return hold at most one thing. */
  item: Carryable | null
  /**
   * Held-progress in ticks for the board (chopping) and sink (washing), frozen
   * at the moment the key was last released. Survives release: interacting
   * again resumes rather than restarting.
   */
  progressTicks: number
  /**
   * How much held time this station's current job takes in total. Carried in
   * state rather than looked up from `timings.json` so a stored log renders its
   * own progress bar correctly even after the numbers are retuned — the log
   * alone reconstructs the round.
   */
  progressTargetTicks: number
  /**
   * Tick at which the current hold began, or `null` when nobody is holding.
   * Live progress is `progressTicks + (tick - resumedAtTick)`, so a hold that
   * runs for three seconds costs two events rather than a hundred and fifty.
   */
  resumedAtTick: number | null
  /** Who is holding the key on this station right now. */
  heldBy: PlayerId | null
  /**
   * *What* the frozen progress belongs to, when the thing being worked on is
   * not the station's own contents.
   *
   * The board needs no such field: what is being chopped is `item`, and putting
   * a different ingredient on the board means picking the old one up, which
   * already clears the progress. The sink is the exception — the plate stays in
   * the washer's hands — so without this, `progressTicks` is progress towards
   * washing *a plate*, and any plate could collect it: nineteen seconds of
   * scrubbing plate A, stow it, pick up dirty plate B, one press, and B comes
   * out clean. The plate id is stamped here when a wash begins, and a hold that
   * does not match starts from zero.
   *
   * `null` for every station that is not mid-wash, and for keyframes recorded
   * before the field existed.
   */
  heldSubject: string | null
  /**
   * Cooking equipment only: the container currently standing on it. `null` is
   * a real state now — a stove with no pan on it cannot be cooked on, and an
   * ingredient offered to it is refused, which is the FSM-of-inclusion rule
   * "ingredients cannot go into static equipment".
   */
  container: Container | null
  /**
   * Time constraint #4. A container left burning on cooking equipment for
   * `burntToFireMs` sets the equipment itself alight, and a burning station
   * refuses every interaction until somebody puts it out with the extinguisher.
   */
  onFire: boolean
  /** Crates only — which ingredient this dispenser produces. */
  dispenses: IngredientId | null
  /** Plate stacks only — clean plates currently available to take. */
  cleanPlates: number
  /**
   * Plate returns only — dirty plates waiting to be carried to the sink.
   *
   * A counter rather than a queue of plates, for the same reason the stack is
   * one: nothing in the game needs a returned plate's identity, and a station
   * that can only hold one dirty plate is a station that blocks the return
   * queue behind it — which is one of the two soft-locks the playtest found.
   */
  dirtyPlates: number
}

/**
 * A served plate on its way back to the plate return. It
 * reappears after a short delay, with no warning and no visible timer.
 */
export interface PendingPlate {
  plateId: string
  dueAtTick: number
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export interface Order {
  id: string
  dishId: string
  spawnedAtTick: number
  expiresAtTick: number
}

/**
 * One line of a map's `// schedule:` directive, per dish: this ticket arrives
 * on this tick. A map that carries a schedule issues exactly these tickets, in
 * this order (sorted by tick, file order within a tick), and no cadence ones;
 * entry `i` becomes `order-i`. Carried in state and in every keyframe, so a
 * stored log replays its own arrivals whatever the server serves today.
 */
export interface ScheduledOrder {
  atTick: number
  dishId: string
}

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

export interface Player {
  id: PlayerId
  /** Spawn index from the map file. Ordering only — never an identity. */
  seat: number
  cell: Cell
  facing: Direction
  holding: Carryable | null
  connected: boolean
}

// ---------------------------------------------------------------------------
// Macro-actions — what a *planner* may ask for.
//
// One level above `PlayerAction` and deliberately not on the wire: the server
// has never heard of a macro, and an agent that emits one still sends the same
// move and interact edges a browser sends. It is in the contract because the
// tape carries it — a `PLAN` event records the macro that produced the next few
// hundred inputs, which is the only way an analyst can tell a model's
// coordination from a pathfinder's luck.
//
// The vocabulary is typed against `recipes.json` ids rather than free text, so
// a malformed macro is a parse failure rather than a wrong move. `say` is the
// single free-form field, because talking is the one thing humans do with words
// in this kitchen too.
// ---------------------------------------------------------------------------

/**
 * Every verb, as a value rather than only as a type — the same trick
 * `STATION_TYPES` plays, and for the same reason: a planner has to be *told*
 * the vocabulary, and a list a schema can be generated from cannot drift from
 * the union a parser checks against.
 */
export const MACRO_VERBS = [
  'fetch',
  'chop',
  'cook',
  /**
   * Moving a vessel between two heat sources, which is a verb because a map may
   * ship an appliance with nothing standing on it.
   *
   * `medium-7x10` starts a pan on each stove and the oven bare, deliberately —
   * the map file says so in as many words — so every baked ingredient in the
   * catalogue needs somebody to carry a pan across first. Without a verb for it
   * the whole `roast_platter` branch of the DAG was unreachable by a planner:
   * seven recorded rounds used the oven zero times, and what the tape shows
   * instead is models inventing container ids at the oven's cell —
   * `cook chopped_mushroom in pan-0-6-3`, ten times in one round, against an
   * oven that had no pan on it and a `pan-0-6-3` that did not exist.
   */
  'carry',
  'plate',
  'serve',
  'wash',
  'bin',
  'extinguish',
  'wait',
  'say',
  // The two social verbs. They arrive with the second planner, because they are
  // the two things a chef only ever does *because of somebody else* — and they
  // exist so that spatial conflict is something a model can say rather than
  // something a pathfinder resolves behind its back.
  'handoff',
  'yield',
] as const

export type MacroVerb = (typeof MACRO_VERBS)[number]

export type Macro =
  /**
   * End up holding one `ingredient`, from wherever one is.
   *
   * A crate, or the counter the other chef left one on — which is the receiving
   * half of `handoff`, and the reason this is not called `take_from_crate`.
   */
  | { verb: 'fetch'; ingredient: IngredientId }
  /** Put `ingredient` on a board, hold the key until it is cut, take it back. */
  | { verb: 'chop'; ingredient: IngredientId }
  /** Put what is in hand into `container`, on whatever equipment it stands on. */
  | { verb: 'cook'; ingredient: IngredientId; container: ContainerId }
  /**
   * Pick an empty `container` up and set it down on the equipment at `to`.
   *
   * The only macro whose subject is a vessel rather than food, and the only one
   * that can change what a kitchen is able to cook: a bare oven cooks nothing,
   * and an oven with a pan on it cooks the two baked ingredients the stove
   * cannot make. A pan with food in it cannot be lifted off the heat, which is
   * the engine's rule and not this verb's — so a `carry` chosen at the wrong
   * moment is refused with a sentence rather than quietly tipping a stew out.
   */
  | { verb: 'carry'; container: ContainerId; to: CellKey }
  /** Move what is in `container` onto a clean plate, fetching one if needed. */
  | { verb: 'plate'; from: ContainerId }
  /** Carry the loaded plate to the hatch. `order` is what it is aimed at. */
  | { verb: 'serve'; order: OrderId }
  | { verb: 'wash' }
  | { verb: 'bin' }
  | { verb: 'extinguish'; station: CellKey }
  /** Stand still. The round does not wait for anybody, including a planner. */
  | { verb: 'wait'; ticks: number }
  /** The only free-form field in the vocabulary. */
  | { verb: 'say'; text: string }
  /**
   * Leave what is in hand on a counter, for the other chef to take.
   *
   * There is no throwing and nothing is dropped on the floor, so a counter is
   * the whole of how two seats give each other anything — which on a split
   * kitchen is the only way the two halves of a dish ever meet.
   *
   * `item` is a statement of intent rather than a selector, exactly as `cook`'s
   * `ingredient` is: the executor puts down whatever the hand is holding, and
   * the argument is what the planner *said* it was handing over, recorded so an
   * analyst can see a handover that turned out to be the wrong thing.
   */
  | { verb: 'handoff'; item: CarriedName; counter: CellKey }
  /**
   * Step aside and let the other chef through.
   *
   * The verb that makes deconfliction attributable. When two chefs untangle in
   * a corridor, the tape either carries a `PLAN` saying one of them chose to
   * give way — this — or it does not, and then it was our pathfinder. Yiheng's
   * objection to the AI-AI condition is exactly that difference, and this is
   * the field that answers it.
   *
   * It is also the fallback once a round runs out of planner budget: a chef with
   * no more thinking to spend still gets out of the way.
   */
  | { verb: 'yield'; to: PlayerId }

// ---------------------------------------------------------------------------
// Inputs — what a client may ask for. The server decides what happens.
// ---------------------------------------------------------------------------

/**
 * The client's own clock at the moment the key was pressed, in epoch
 * milliseconds (`performance.timeOrigin + performance.now()`).
 *
 * It is carried for the tape only — nothing in the simulation reads it, and a
 * client that omits it is not refused. The server stamps its own arrival time
 * in that case, so an `INPUT` event always has a number; what the field buys
 * when it *is* present is the difference between the two, which is input
 * latency and hesitation measured at the only place that can see them.
 */
export type ClientStampedAction = { clientMs?: number }

export type PlayerAction =
  | ({ kind: 'move'; direction: Direction } & ClientStampedAction)
  /** The key went down. The full section 5 dispatch runs on this edge only. */
  | ({ kind: 'interact' } & ClientStampedAction)
  /** The key came up. Freezes any held progress; the engine never sees it. */
  | ({ kind: 'interact_release' } & ClientStampedAction)
  /**
   * Synthesised by the transport on every tick between a press and its release,
   * so the engine can advance chop and wash progress without the client
   * shouting fifty times a second. Clients never send this; `isValidAction`
   * rejects it if one tries.
   */
  | { kind: 'interact_hold' }

export interface PlayerInput {
  playerId: PlayerId
  action: PlayerAction
}

/**
 * Why the transport refused an input, before the engine ever saw it.
 *
 * These are not `ACTION_FAILED` reasons and must never enter the event tape:
 * the tape is the replay, and a refusal that was never simulated did not
 * happen in the kitchen. They travel to the one socket that sent the input, so
 * that a player pressing keys into a void is told the void is there.
 */
export type InputRejectionReason =
  /** More than the per-second allowance. A script, or a key repeat gone wrong. */
  | 'rate_limited'
  /** No seat on this socket yet — the join has not landed. */
  | 'no_seat'
  /** There is no round running to send input to. */
  | 'not_playing'
  /** Not a shape the server accepts — including `interact_hold`, which is the loop's. */
  | 'malformed'
  /**
   * A move from an agent seat that came sooner than `agentMoveIntervalMs` after
   * its last accepted move. Human seats never get this.
   */
  | 'agent_move_paced'

// ---------------------------------------------------------------------------
// Events — the single stream that is both the wire format and the log format.
//
// Every event carries `tick`. `wallClock` exists only to align the log against
// the audio recording afterwards; the game itself trusts `tick` alone
//.
// ---------------------------------------------------------------------------

export interface EventBase {
  tick: number
  wallClock: number
  /** A player id, or `system` for autonomous game events. */
  actor: PlayerId | 'system'
}

export type GameEvent = EventBase &
  (
    /**
     * The round opener, and the only anchor a client has for its own clock.
     *
     * `startedAtEpochMs` is the wall-clock instant tick 0 was simulated at —
     * `room.roundStartWallClock`, the same number the loop measures elapsed
     * ticks against. A client that knows it can compute the current tick from
     * its own clock instead of extrapolating from whenever the last frame
     * happened to arrive, which is what stops a chop bar pinning full during
     * the three seconds a hold emits nothing.
     *
     * It is an epoch stamp rather than a duration because the two clocks are
     * different machines': the client converts it into an origin on its own
     * monotonic timeline once, at anchor time, and never compares epochs again.
     */
    | {
        type: 'ROUND_STARTED'
        mapName: string
        players: Player[]
        durationTicks: number
        startedAtEpochMs: number
      }
    | {
        type: 'ROUND_ENDED'
        reason: 'timer' | 'aborted'
        score: number
        /**
         * What the round amounted to, counted off this same tape by the server
         * at the whistle: dishes served, matched, unmatched, expired, the score
         * by source and per-seat activity. Optional — a tape recorded before
         * version 6 has none, and `summariseRound` over its frames gives the
         * same numbers.
         */
        summary?: RoundSummary
      }
    /**
     * 3 … 2 … 1. The seconds between "everybody is in" and the first tick.
     *
     * Emitted while the room is still in the lobby, which is the whole point:
     * the trace does not exist yet, so this is the one event type that can
     * never appear in a `log.jsonl` and is deliberately absent from
     * `schema/session-log.schema.json`. Tick 0 stays the first tick of play and
     * `syncBeepTick` stays 0, so nothing the replay aligns on moves.
     *
     * The fold declines it for the same reason it declines `INPUT`: a cue that
     * precedes the round must not be able to advance the round's clock.
     */
    | { type: 'COUNTDOWN'; secondsRemaining: number }
    /** Audible anchor at tick 0 so the audio track can be aligned. */
    | { type: 'SYNC_BEEP' }
    | { type: 'PLAYER_MOVED'; playerId: PlayerId; to: Cell; facing: Direction }
    | { type: 'PLAYER_TURNED'; playerId: PlayerId; facing: Direction }
    | { type: 'PLAYER_BLOCKED'; playerId: PlayerId; cell: Cell }
    /** Two players tried to enter the same cell on the same tick. 💥 */
    | { type: 'COLLISION'; cell: Cell; playerIds: PlayerId[] }
    /** Two players acted on the same station on the same tick. Both fail. */
    | { type: 'CONTESTED'; cell: Cell; playerIds: PlayerId[] }
    | { type: 'PICKED_UP'; playerId: PlayerId; cell: Cell; item: Carryable }
    | { type: 'PUT_DOWN'; playerId: PlayerId; cell: Cell; item: Carryable }
    /** Emitted when a hold starts and when it stops — never once per tick. */
    | {
        type: 'CHOP_PROGRESS'
        playerId: PlayerId
        cell: Cell
        progressTicks: number
        targetTicks: number
        resumedAtTick: number | null
      }
    | { type: 'CHOPPED'; playerId: PlayerId; cell: Cell; from: IngredientId; to: IngredientId }
    | {
        type: 'WASH_PROGRESS'
        playerId: PlayerId
        cell: Cell
        progressTicks: number
        targetTicks: number
        resumedAtTick: number | null
      }
    | { type: 'WASHED'; playerId: PlayerId; cell: Cell; plateId: string }
    | { type: 'PAN_FILLED'; playerId: PlayerId; cell: Cell; ingredient: IngredientId }
    /**
     * The one authoritative container delta. It carries the resulting contents
     * and both phase stamps so a replay never has to consult `recipes.json` or
     * `timings.json` — the log alone reconstructs the round.
     *
     * `containerId` is what the fold locates: a container is no longer welded
     * to a cell, so addressing it by cell alone would lose it the moment
     * somebody picked it up.
     */
    | {
        type: 'COOK_STATE_CHANGED'
        cell: Cell
        containerId: string
        state: CookState
        contents: IngredientId[]
        startedAtTick: number | null
        endsAtTick: number | null
      }
    | { type: 'PAN_EMPTIED'; playerId: PlayerId | 'system'; cell: Cell; wasBurnt: boolean }
    /**
     * Time constraint #4: the equipment itself is alight. Until it is put out,
     * every interaction with this station fails — including taking the ruined
     * container off it.
     */
    | { type: 'FIRE_STARTED'; cell: Cell }
    | {
        type: 'EXTINGUISH_PROGRESS'
        playerId: PlayerId
        cell: Cell
        progressTicks: number
        targetTicks: number
        resumedAtTick: number | null
      }
    | { type: 'FIRE_EXTINGUISHED'; playerId: PlayerId; cell: Cell }
    /**
     * **Retired.** Carried equipment left a player's hands and appeared on its
     * rack, with nobody having walked to the rack.
     *
     * It used to be emitted when a fire went out and when the extinguisher was
     * taken to the bin, both on the theory that the kitchen's fixed possessions
     * should send themselves home rather than strand a player. A playtester
     * reported the first of those as a bug — the extinguisher teleporting out
     * of his hands — and he was right: nothing in this kitchen moves without
     * somebody carrying it. The extinguisher is now walked back, which is an
     * ordinary `PUT_DOWN`, and the bin refuses it with `rack_the_extinguisher`.
     *
     * The variant stays in the union, and `applyEvent` still folds it, because
     * session logs recorded before that change have to keep replaying. Nothing
     * emits it.
     */
    | { type: 'EQUIPMENT_RACKED'; playerId: PlayerId; cell: Cell; equipmentId: EquipmentId }
    /**
     * **Retired.** A plate left a player's hands and joined a pile somewhere
     * else.
     *
     * The plate twin of `EQUIPMENT_RACKED`, retired with it and for the same
     * reason: binning an empty plate teleported it to the clean stack or the
     * washing-up pile. The bin now refuses it with `plates_go_home`, and the
     * plate count is conserved by the walk instead — the stack and the sink are
     * both always able to take one, which is what `validateMap` guarantees.
     *
     * Kept in the union so old logs still fold. Nothing emits it.
     */
    | { type: 'PLATE_STOWED'; playerId: PlayerId; cell: Cell; plateId: string }
    | { type: 'PLATED'; playerId: PlayerId; cell: Cell; plateId: string; contents: IngredientId[] }
    | {
        type: 'SERVED'
        playerId: PlayerId
        plateId: string
        contents: IngredientId[]
        matchedOrderId: string | null
        ticketTicksRemaining: number
        scoreDelta: number
        /** When the dirty plate is due back at the plate return. */
        returnsAtTick: number
      }
    | { type: 'TRASHED'; playerId: PlayerId; cell: Cell }
    | { type: 'PLATE_RETURNED'; cell: Cell; plateId: string }
    | { type: 'ORDER_SPAWNED'; order: Order }
    | { type: 'ORDER_EXPIRED'; orderId: string; scoreDelta: number }
    | { type: 'ACTION_FAILED'; playerId: PlayerId; reason: string }
    | { type: 'PLAYER_CONNECTED'; playerId: PlayerId }
    | { type: 'PLAYER_DISCONNECTED'; playerId: PlayerId }
    /** Periodic WebRTC round-trip sample, recorded but never gating. */
    | { type: 'NETWORK_SAMPLE'; playerId: PlayerId; rttMs: number }
    /**
     * What the player actually pressed — an observation, not a simulation.
     *
     * Every other event on the tape is an outcome: `PLAYER_MOVED` says where
     * the chef ended up, never that the left key was held into a wall. This
     * one says the key, the edge, and when the client's own clock thought it
     * happened, so a reviewer can see hesitation and mashing rather than a
     * clean path.
     *
     * The fold ignores it entirely (`fold.ts`), which is what keeps the tape
     * deterministic: a log with these events replays to the identical per-tick
     * state sequence as one with them stripped out.
     *
     * Rejected inputs — `rate_limited`, `no_seat`, `not_playing`, `malformed` —
     * are deliberately not here. See `InputRejectionReason`.
     */
    | {
        type: 'INPUT'
        playerId: PlayerId
        kind: 'move' | 'interact' | 'interact_release'
        /** Present on `move` only. */
        direction?: Direction
        /** The client's clock, or the server's arrival time if it sent none. */
        clientMs: number
      }
    /**
     * Somebody said something.
     *
     * The human-human condition puts speech in a `.webm` beside the log and
     * aligns it on `SYNC_BEEP`. An agent has no microphone, so its half of the
     * conversation has nowhere else to live — and putting it on the tape means
     * the replay page reads a conversation out of the file it already opens,
     * with nothing to drift against because there is no audio track.
     *
     * `channel` says how it left the speaker: `text` for the AI-AI condition,
     * which carries no audio stack at all, and `tts` for a `say` that was
     * spoken aloud into a human's ears. `latencyMs` is how long the speaker
     * took to produce it — for a model, the call; for a human, nothing, because
     * a human's timing is already in the audio.
     *
     * Ignored by the fold, exactly as `INPUT` is. The other agent reads it, the
     * simulation does not.
     */
    | {
        type: 'UTTERANCE'
        playerId: PlayerId
        text: string
        channel: 'text' | 'tts'
        latencyMs: number
      }
    /**
     * The macro-action a planner chose, and what it cost.
     *
     * This is the event that makes coordination attributable. A `CONTESTED` on
     * the tape says two chefs reached for the same station; the `PLAN` next to
     * it says whether the one that gave way *decided* to, and why. Without it,
     * an analyst comparing the AI-AI condition to the human one is measuring
     * our pathfinder and calling it a model.
     *
     * The token counts and the model id ride along so cost per trajectory is a
     * column in the data rather than something reconstructed from a billing
     * dashboard a month later. A scripted seat writes zeros and names itself.
     *
     * Ignored by the fold, exactly as `INPUT` is.
     */
    | {
        type: 'PLAN'
        playerId: PlayerId
        macro: Macro
        /** The planner's own words for why. Absent when it did not say. */
        reason?: string
        /**
         * What the call was answering — the coalesced re-plan triggers, in the
         * planner's own wording, as they were put in front of the model.
         *
         * The half of the attribution the model cannot supply. `reason` is what
         * the model says it did; this is what it was told, and the two together
         * are what turn "the agents deconflicted" into a checkable claim. It
         * exists because the conflict that matters most on a shared-floor map
         * never reaches the engine at all: the executor stops one cell *before*
         * walking into the other chef, so there is no `COLLISION` to sit beside
         * the `PLAN` — the conflict is a route this seat refused to take, and
         * that sentence lived only in a console nobody kept.
         *
         * Absent on a seat with nothing to answer, and on every tape recorded
         * before the field existed.
         */
        answering?: string[]
        /** The model id, or a stand-in like `scripted` for a seat with no model. */
        model: string
        latencyMs: number
        promptTokens: number
        completionTokens: number
      }
    /**
     * A seat's voice link changed state, as that seat's browser saw it.
     *
     * Version 6. Recorded because "we could not hear each other" is otherwise
     * invisible in the data: the audio files say what each microphone heard,
     * and nothing said whether the other person could hear it. Ignored by the
     * fold, exactly as `INPUT` is.
     */
    | {
        type: 'VOICE_STATE'
        playerId: PlayerId
        state: 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed'
        /** Whether the link is relayed through TURN. Absent when unknown. */
        relayed?: boolean
      }
    /** Full state, emitted on join and every KEYFRAME_INTERVAL_TICKS. */
    | { type: 'KEYFRAME'; state: SerialisedState }
  )

export type GameEventType = GameEvent['type']

// ---------------------------------------------------------------------------
// Serialised state — the keyframe payload, and what replay seeks to.
// ---------------------------------------------------------------------------

export interface SerialisedState {
  tick: number
  mapName: string
  players: Player[]
  stations: Station[]
  orders: Order[]
  /** Served plates in flight back to the plate return. */
  pendingPlates: PendingPlate[]
  score: number
  roundDurationTicks: number
  /**
   * The dishes this round's map can be asked for. Carried in the keyframe so a
   * stored log describes its own kitchen, whatever the server serves today.
   */
  orderPool: string[]
  /**
   * The map's order schedule, when it has one. Absent (every keyframe written
   * before schedules existed, and every map without one) means the regular
   * cadence over `orderPool`.
   */
  orderSchedule?: ScheduledOrder[]
}

/** One frame on the wire: everything produced by a single tick. */
export interface EventFrame {
  tick: number
  events: GameEvent[]
}

// ---------------------------------------------------------------------------
// Voice
//
// The media path is browser-to-browser WebRTC; the server only mints ICE
// credentials and relays three kinds of signal between two sockets in one
// room. Nothing here assumes a provider.
// ---------------------------------------------------------------------------

/** Exactly the shape `new RTCPeerConnection({ iceServers })` wants. */
export interface IceServer {
  urls: string[]
  username?: string
  credential?: string
}

export interface IceConfig {
  iceServers: IceServer[]
  /** Epoch ms after which these credentials stop working. */
  expiresAt: number
  /** Which provider minted them — surfaced in `/api/ready` and the logs. */
  provider: string
}

/**
 * The only three messages that cross the wire during negotiation. `description`
 * carries both offers and answers because perfect negotiation treats them the
 * same way on receipt.
 */
export type RtcSignal = (
  | { kind: 'description'; description: { type: 'offer' | 'answer'; sdp: string } }
  | { kind: 'candidate'; candidate: unknown }
  /** Sent by a peer that has just restarted ICE, so the other side stops waiting. */
  | { kind: 'bye' }
) &
  RtcSignalSession

/**
 * Which peer connection a signal belongs to, at both ends. Optional and
 * additive: a client that sends neither is treated as it always was, and the
 * server relays the envelope without reading them.
 *
 * `session` names the sender's `RTCPeerConnection`, minted fresh for each one.
 * `for` names the receiver's connection the sender believes it is talking to,
 * or `null` when it has not heard from one yet. Together they let both ends
 * agree when either has started a new connection — a reload, or a rebuild —
 * since a connection's DTLS keys cannot be renegotiated into another's.
 */
export interface RtcSignalSession {
  session?: string
  for?: string | null
}

/** Latency gate and voice-sampling parameters, read from `timings.json`. */
export interface LatencyConfig {
  gateSampleCount: number
  gateSampleTimeoutMs: number
  gateMaxMedianRttMs: number
  voiceSampleIntervalMs: number
}

/**
 * What a ticket is asking for, so the HUD can name and picture a dish without
 * shipping a copy of `recipes.json`. An order carries only a `dishId`; this is
 * the lookup, served from the same content the engine reads.
 */
export interface DishSummary {
  id: string
  label: string
  contents: IngredientId[]
  /**
   * What a player has to *do* to make this, derived from the recipe DAG on the
   * server. An order ticket is a recipe card, and a recipe card cannot
   * be drawn from `contents` alone — `stewed_carrot` does not tell anybody that
   * a carrot comes out of a crate, gets chopped, and stews in a pan.
   *
   * Derived, never tabulated: adding a dish stays a `recipes.json` edit.
   */
  requires: DishRequirement
  /**
   * Extra points a matched serve of this dish pays (`recipes.json`
   * `dishes[].bonus`), so the recipe card can say a stew is worth more.
   * The server always sends it (0 for a dish with none); a catalogue pinned
   * into a log header from before bonuses existed has no field, which reads
   * as 0.
   */
  bonus?: number
}

/** One item on the plate, traced back to the crate it starts life in. */
export interface RequiredItem {
  /** The finished ingredient on the plate, e.g. `stewed_carrot`. */
  id: IngredientId
  /** The crate ingredient it descends from, e.g. `carrot`. */
  source: IngredientId
  /** Static equipment it passes through, in order, e.g. `['board', 'stove']`. */
  stations: StationType[]
  /** The vessel it must be cooked in, or `null` if it is never cooked. */
  container: ContainerKind | null
}

export interface DishRequirement {
  /** One entry per plate item, in the dish's own order. Repeats are repeats. */
  items: RequiredItem[]
  /** Distinct vessels the dish needs at least one of. */
  containers: ContainerKind[]
  /** Distinct static equipment the dish needs, excluding crates and the hatch. */
  stations: StationType[]
}

/**
 * An ingredient, and the one row of the DAG that produces it.
 *
 * This is what lets the renderer draw fourteen distinguishable ingredients
 * without knowing any of their names: `chopped_carrot` is "whatever a carrot
 * looks like, cut up", because the server said it comes off a board from a
 * carrot. A fifteenth ingredient needs no client change.
 */
export interface IngredientSummary {
  id: IngredientId
  label: string
  /** `recipes.json`'s own flag: is this edible-as-is or does it still need heat. */
  raw: boolean
  /** `null` for a source ingredient — one that comes out of a crate. */
  from: {
    station: StationType
    ingredient: IngredientId
    /** Set only when the step happens inside a vessel. */
    container: ContainerKind | null
  } | null
}

/** What the client needs before it may join a room. `GET /api/config`. */
/** One shippable map preset, so a lobby can offer a choice without a rebuild. */
export interface MapSummary {
  name: string
  width: number
  height: number
  /** Dish ids this layout is solvable for. */
  orderPool: string[]
  /**
   * How many spawns the layout declares — which is how many people it is for.
   * The solo kitchen has one, and a lobby offering a choice of kitchens for two
   * needs to be able to leave it out without knowing its name.
   */
  seats: number
}

export interface ClientConfig {
  latency: LatencyConfig
  playerCount: number
  dishes: DishSummary[]
  /**
   * The whole ingredient DAG as data, so the renderer can draw an icon for
   * anything `recipes.json` names without carrying a copy of it.
   */
  ingredients: IngredientSummary[]
  /** Every map preset the server has loaded, and which one it serves by default. */
  maps: MapSummary[]
  defaultMap: string
  /**
   * How long the server will wait for every seat to report ready before it
   * starts the first round anyway. A client that never readies must not be able
   * to strand the other player in an empty lobby.
   */
  readyTimeoutMs: number
  /**
   * How long a client waits for voice to settle before it reports ready. Spec
   * section 14 wants voice connected before the round timer starts; this bounds
   * that wish so a failed negotiation costs a few seconds, not the session.
   */
  voiceReadyGraceMs: number
}

// ---------------------------------------------------------------------------
// Recording consent
//
// A person in a multiplayer room is recorded — their microphone and every move
// they make — so nobody is seated there without having agreed to it. The
// browser asks; the server refuses a seat without the answer and writes the
// answer down beside the recording it permits.
// ---------------------------------------------------------------------------

/**
 * The wording the player agreed to. Bump it whenever the consent screen's text
 * changes: a stored agreement to older wording no longer counts, so everyone is
 * asked again.
 */
export const CONSENT_VERSION = 1

/** What the browser sends: which wording was agreed to, and when. */
export interface ConsentRecord {
  version: number
  /** ISO — the moment the player pressed "Agree and continue", by their clock. */
  acceptedAt: string
  /**
   * Which consent text `version` numbers. Absent is the friends-mode screen,
   * versioned by `CONSENT_VERSION`; `study` is the Prolific study consent,
   * versioned by `STUDY_CONSENT_VERSION`. Only the server seats a study
   * participant, so a `joinRoom` never admits a study record.
   */
  variant?: 'study'
}

/** A consent record as the server keeps it, stamped when the seat was taken. */
export interface StoredConsent extends ConsentRecord {
  /** ISO — when the server accepted the record and seated the player. */
  receivedAt: string
}

/**
 * The `errorMessage` a join is refused with when consent is missing or stale.
 * Shared so the client can recognise it and ask again rather than just show it.
 */
export const CONSENT_REFUSED = 'Please agree to recording before joining.'

/**
 * The `errorMessage` an agent seat is refused with when it carries no valid
 * agent token for the room. An agent is let in without a consent record, so
 * the claim to be one has to be signed by the server (or by an operator holding
 * its `AGENT_TOKEN_SECRET`) rather than taken on trust.
 */
export const AGENT_TOKEN_REFUSED = 'This AI chef was not started by this server.'

// ---------------------------------------------------------------------------
// Session artifacts
//
// A stored session is three things under one key prefix: the log that went on
// the wire, one audio file per player, and a manifest that describes both. The
// manifest exists so a session list opens no logs — a hundred sessions is a
// hundred small JSON reads, not a hundred multi-megabyte ones.
// ---------------------------------------------------------------------------

/** An after-the-fact quality label, never a live gate on a player. */
export type AudioStatus = 'ok' | 'partial' | 'invalid_signature' | 'upload_failed' | 'missing'

export interface AudioArtifact {
  playerId: PlayerId
  seat: number
  /** Blob key, or `null` when the player sent nothing. */
  key: string | null
  mimeType: string
  bytes: number
  chunkCount: number
  status: AudioStatus
  /** Why the status is not `ok`. Empty when it is. */
  flags: Array<{ event: string; detail: string }>
  /**
   * Every file this seat's recording ended up in, in the order they started.
   *
   * A recorder restarts when its page is reloaded mid-round, and the second
   * recording is a new container with its own header — splicing it onto the
   * first would make a file no decoder opens past the join. So each restart is
   * its own segment and its own object. The first segment is always the one at
   * `key` above, which is what every older reader plays; `bytes`/`chunkCount`
   * above describe that first file too. Absent when there was at most one.
   */
  segments?: AudioSegment[]
  /**
   * With `segments`, the whole recording across every file; `bytes` and
   * `chunkCount` above stay the first file's, as every older reader expects.
   * Absent when there was one file.
   */
  totalBytes?: number
  totalChunkCount?: number
  /**
   * Whether the seat sent its end-of-recording accounting (`audioDone`). A
   * `false` here is a recording whose last second may be missing. Absent on
   * artifacts written before the field existed.
   */
  reported?: boolean
  /**
   * Whether the recording carries the sync beep. `false` when the browser
   * recorded the bare microphone (its audio graph was suspended), in which
   * case the file aligns by `firstChunkTick` rather than by the tone. Absent
   * when unknown.
   */
  syncBeep?: boolean
}

/**
 * The server's answer to one `audioChunk`.
 *
 * `ok` means the bytes are held (or were already held — a resend is
 * idempotent). `retry` means "not now, send it again": the socket has no seat
 * yet (a reconnect racing its own rejoin) or was rate limited. Neither means a
 * chunk that will never be accepted, which is dropped by the client.
 */
export interface AudioChunkAck {
  ok: boolean
  retry?: boolean
}

/** One recorder run within a seat's recording. See `AudioArtifact.segments`. */
export interface AudioSegment {
  /** The client's id for this recorder run, or `null` for an older client. */
  recordingId: string | null
  key: string | null
  mimeType: string
  bytes: number
  chunkCount: number
  status: AudioStatus
  flags: Array<{ event: string; detail: string }>
  /** The round tick the server received this segment's first and last chunk on. */
  firstChunkTick: number | null
  lastChunkTick: number | null
  reported: boolean
  syncBeep?: boolean
  /** Where this one file is served: `…/audio/{playerId}?segment={index}`. */
  url?: string
}

export interface SessionManifest {
  version: 1
  sessionId: string
  roomCode: string
  mapName: string
  /** ISO — when the round started, and when it finished. */
  startedAt: string
  endedAt: string
  reason: 'timer' | 'aborted'
  durationTicks: number
  /** The last tick the log carries. Differs from `durationTicks` on an abort. */
  lastTick: number
  frameCount: number
  eventCount: number
  score: number
  /**
   * Which study condition the round was, from the seats that played it.
   * `null` when nothing said who was sitting there — a manifest written before
   * this field existed, or a finalise that was handed no participants.
   */
  condition: StudyCondition | null
  /**
   * The invite code — the intake batch — the round was played under. `null`
   * for a room opened with the invite check off, and absent on a manifest
   * written before the field existed, which every reader treats as `null`.
   */
  invite?: string | null
  /**
   * `kind`/`model` are `null` for the same reason `condition` can be: a seat
   * whose occupant was never declared is unknown, not silently a person.
   */
  players: Array<{
    id: PlayerId
    seat: number
    name: string | null
    kind: SeatKind | null
    model: string | null
    /**
     * The recording agreement this seat was admitted on. `null` for an agent
     * seat, a solo room, or a manifest written before consent was recorded.
     */
    consent: StoredConsent | null
    /** `false` for an agent seat that ran with talking off. Absent means it could talk. */
    talk?: false
  }>
  log: { key: string; lines: number }
  audio: AudioArtifact[]
  /**
   * The tick the audible anchor was emitted on — 0 in every well-formed
   * session, and carried explicitly so an analyst aligning audio never has to
   * assume it.
   */
  syncBeepTick: number | null
  /**
   * Which game of which study dyad this round was. Absent on every round that
   * was not played in study mode, and on every manifest written before study
   * mode existed. Internal ids only — never a Prolific id.
   */
  study?: SessionStudyBlock
  /**
   * The round's outcome — dishes served, matched, unmatched, expired, score by
   * source, per-seat activity — counted off the stored log. Absent on a
   * manifest written before the field existed; `summariseRound` over the log
   * reproduces it.
   */
  summary?: RoundSummary
  /**
   * Whether the data a researcher needs from this round actually exists, seat
   * by seat, with the reasons. A round is never dropped for being incomplete —
   * it is stored and labelled, and this is the label. Absent on a manifest
   * written before the field existed.
   */
  dataQuality?: SessionDataQuality
  /**
   * Set when the round was ended by the server rather than by the room: a
   * deploy or restart (`shutdown`), or a process that died mid-round and was
   * written from its crash spool (`crash`). Absent otherwise.
   */
  endedBy?: 'shutdown' | 'crash'
}

/** One row of the top-level index. Enough to choose a session, and no more. */
export interface SessionIndexEntry {
  sessionId: string
  roomCode: string
  startedAt: string
  endedAt: string
  reason: 'timer' | 'aborted'
  score: number
  lastTick: number
  frameCount: number
  playerCount: number
  audioStatus: AudioStatus[]
  /** The replay picker's tab. `null` for a session nobody has classified. */
  condition: StudyCondition | null
  /**
   * The batch filter's key. `null` (or absent, on an index written before the
   * field existed) is "No batch": a room opened without a code, or a round
   * recorded before codes existed.
   */
  invite?: string | null
  /** Who sat where, in seat order — enough to name the chefs in a list row. */
  seats: Array<{ name: string | null; kind: SeatKind | null; model: string | null }>
  /**
   * The recording agreement behind this round, summarised for the admin list:
   * which wording versions were agreed to, and how many of the human seats
   * carried one. Absent on a row written before consent was recorded.
   */
  consent?: { versions: number[]; humans: number; agreed: number }
  /**
   * The manifest's data-quality verdict, folded down for a list: whether
   * anything was lost, whether the audio is whole, and which flags were raised.
   * Absent on a row written before the field existed (a rebuild fills it in).
   */
  quality?: {
    status: SessionDataQuality['status']
    audio: SessionDataQuality['audio']['status']
    flags: DataQualityCode[]
  }
  /** Dishes served, matched and expired, from the manifest's summary. */
  dishes?: { served: number; matched: number; unmatched: number; expired: number }
}

export interface SessionIndex {
  version: 1
  updatedAt: string
  sessions: SessionIndexEntry[]
}

// ---------------------------------------------------------------------------
// The session log header — what makes one JSONL file enough
//
// A `log.jsonl` used to need two companions to be watchable: the running
// server's *current* `/api/config`, so the icons meant anything, and the
// session's `manifest.json`, for the map, the players and the recordings. Send
// somebody the log on its own and they got a kitchen full of anonymous shapes.
//
// The header is the first line of the file and it carries both. It is not a
// game event and it does not pretend to be one — hence its own `kind`, rather
// than a `SESSION_HEADER` entry in `GameEvent` that every fold would then have
// to decline. `head -1 log.jsonl` is the whole reading instruction.
//
// The catalogue is *pinned*: it is the content as it stood when the round was
// played, so retuning a recipe next month cannot silently re-skin a recording
// made today. That is the one thing the live endpoint could never give.
// ---------------------------------------------------------------------------

/**
 * The version this build writes. A file with no header line is version 1 — the
 * shape recorded before the header existed — and readers must keep handling it,
 * because the recordings already in the bucket are not going to grow one.
 *
 * Version 2 is this header without `condition` and `participants`. It parses
 * unchanged and replays unchanged; the only thing it cannot do is say which of
 * the three study conditions it was, which is a question nobody asked of a
 * round recorded before there were three. Both fields land in one step rather
 * than two because `UTTERANCE` and `PLAN` forced a version bump anyway.
 *
 * Version 4 is version 3 plus `invite`: the invite code the room was opened
 * under, or `null` for a room opened with the invite check off. A version-1 to
 * version-3 file has no such field and reads as `null` — no batch — because
 * nothing recorded one, and a batch is never guessed after the fact.
 *
 * Version 5 is version 4 plus `consent` on each participant: the recording
 * agreement the seat was taken on, or `null` for an agent seat or a solo room.
 * A version-4 file has none, and a reader treats its seats as unknown on that
 * question rather than refused.
 *
 * Version 6 is version 5 plus, all additive:
 *
 *   - `condition: 'solo'` for a one-seat room (version 5 wrote `human-human`
 *     there, which a reader should treat as solo when only one seat played);
 *   - `summary` on line 1, amended at the whistle like `cost`, and on the
 *     `ROUND_ENDED` event — the round's dishes and score counted off the tape;
 *   - the `VOICE_STATE` event, ignored by the fold.
 *
 * Every version-2 to version-5 file still parses and replays unchanged.
 */
export const LOG_SCHEMA_VERSION = 6

/**
 * Which of the three study conditions this round was.
 *
 * Recorded rather than inferred. The alternative — deciding after the fact from
 * whether a seat has a `.webm` — cannot tell an agent seat apart from a human
 * whose microphone failed, which is precisely the pair of cases that must never
 * be confused when thirty trajectories are sorted into three buckets of ten.
 */
export type StudyCondition = 'human-human' | 'human-ai' | 'ai-ai' | 'solo'

/**
 * What a log header may say it was: one of the study conditions, or a round the
 * oracle played (`@overcooked/oracle`, both seats planned together).
 *
 * Wider than `StudyCondition` on purpose, and only on the log. An oracle round
 * is stored under `oracle/`, never `sessions/`, so no manifest, session index,
 * export or Prolific analysis ever meets one — and `SessionManifest.condition`
 * stays a `StudyCondition`, so nothing downstream of the index can be handed
 * one by mistake.
 */
export type LogCondition = StudyCondition | 'oracle'

/** Whether a seat was a person or a program. */
export type SeatKind = 'human' | 'agent'

/** Who sat in a seat, and — when it was not a person — what was driving it. */
export interface SessionParticipant {
  playerId: PlayerId
  seat: number
  kind: SeatKind
  /**
   * The model the seat was played by. `null` for a human, and `null` for an
   * agent seat with no model behind it — a scripted one names itself here.
   */
  model: string | null
  /**
   * The recording agreement the seat was admitted on. `null` for an agent and
   * for a person in a solo room, where no consent is asked for. A version-4
   * header and older has no such field, so a reader of old logs must not
   * assume it.
   */
  consent: StoredConsent | null
  /**
   * `false` for an agent seat that ran with talking switched off. Absent means
   * the seat could talk, which is every seat before the switch existed — so it
   * is written only as `false` and old headers need no reading rule. Per seat
   * rather than per round, so a room with one talking and one silent agent
   * says exactly that.
   */
  talk?: false
}

/**
 * Every seat's kind, from the seats as the room knows them.
 *
 * One rule in one place, so the header, the manifest and anything downstream
 * cannot each decide a mixed round is something different. Zero seats is
 * `human-human`: a round nobody joined is not an AI round.
 */
export function conditionOf(participants: Array<{ kind: 'human' | 'agent' }>): StudyCondition {
  const agents = participants.filter((p) => p.kind === 'agent').length
  // One person on their own is not a pair. Version 5 and older wrote
  // `human-human` here; a solo round has no partner and no recording.
  if (agents === 0 && participants.length === 1) return 'solo'
  if (agents === 0) return 'human-human'
  if (agents === participants.length) return 'ai-ai'
  return 'human-ai'
}

/**
 * Every knob from `timings.json`, verbatim, as the header carries it.
 *
 * Typed loosely on purpose. The contract package must not import the engine's
 * content module, and the header's job is to record the file *as it was on the
 * day* — including keys a later build renamed or retired — rather than to
 * re-describe a shape that is data. `KNOBS.md` is where the keys are named;
 * this is where they are preserved.
 */
export type LoggedTimings = Record<string, unknown>

/**
 * What one seat's thinking cost, as the seat itself reported it.
 *
 * Per seat rather than per round, because "per model" is the split that matters:
 * a round with a cheap model in one chair and an expensive one in the other has
 * two prices, and a single total hides which of them the competence came from.
 * A human seat and a scripted seat report nothing and appear here not at all.
 */
export interface SeatCost {
  playerId: PlayerId
  seat: number
  /** The model id, verbatim — the same string the seat's `PLAN` events carry. */
  model: string
  calls: number
  /** Calls that came back unreadable, and calls that never came back. */
  parseFailures: number
  callFailures: number
  promptTokens: number
  /** The part of `promptTokens` the provider served from its cache. */
  cachedPromptTokens: number
  /**
   * Whether the provider reported a cache split at all.
   *
   * `false` means `cachedPromptTokens: 0` is silence rather than a measurement,
   * and a reader must not report it as "nothing cached". Absent on logs written
   * before this field existed, where the honest reading is also "unknown".
   */
  cacheReported?: boolean
  completionTokens: number
  /** Dollars, as the provider charged them. Zero when it did not say. */
  costUsd: number
  /** Time spent waiting on the model. Not the round's wall clock. */
  thinkingMs: number
  /** Whether this seat ran into its per-round call ceiling. */
  ceilingHit: boolean
}

/**
 * The round's bill, on line 1.
 *
 * Every session's first line already says which condition it was, who sat in
 * each seat and which model; this is the last of the four — what the round cost.
 * It is written by rewriting the header at `finaliseSession`, because the bill
 * does not exist until the round is over and the header goes out with the
 * trace's first flush.
 *
 * Absent on a round nobody paid for, which is every human-human round and every
 * version-3 log recorded before this field existed. A reader that finds no
 * `cost` has found a free round or an old one, and both are true statements.
 */
export interface RoundCost {
  calls: number
  promptTokens: number
  cachedPromptTokens: number
  /** `promptTokens - cachedPromptTokens`, carried so nobody subtracts wrongly. */
  uncachedPromptTokens: number
  /**
   * Whether any seat's provider reported a cache split.
   *
   * False means the two numbers above are a floor and a ceiling rather than a
   * split, and the header must not be read as "this round cached nothing".
   */
  cacheReported?: boolean
  completionTokens: number
  costUsd: number
  /** One entry per seat that spent anything. The per-model split. */
  seats: SeatCost[]
}

/** Add up what the seats reported. One rule, in one place. */
export function totalCost(seats: SeatCost[]): RoundCost {
  const sum = (read: (seat: SeatCost) => number): number =>
    seats.reduce((total, seat) => total + read(seat), 0)
  const promptTokens = sum((s) => s.promptTokens)
  const cachedPromptTokens = sum((s) => s.cachedPromptTokens)
  // Said at all by anybody, rather than said by everybody: one seat that knows
  // its split is enough for the round's numbers to mean something. Left off
  // entirely when no seat declared either way, so an old log and a silent
  // provider read the same — unknown.
  const declared = seats.filter((s) => s.cacheReported !== undefined)
  return {
    calls: sum((s) => s.calls),
    promptTokens,
    cachedPromptTokens,
    uncachedPromptTokens: Math.max(0, promptTokens - cachedPromptTokens),
    ...(declared.length > 0
      ? { cacheReported: declared.some((s) => s.cacheReported === true) }
      : {}),
    completionTokens: sum((s) => s.completionTokens),
    costUsd: sum((s) => s.costUsd),
    seats: [...seats].sort((a, b) => a.seat - b.seat),
  }
}

/** The first line of a `log.jsonl`. Every other line is an `EventFrame`. */
export interface SessionLogHeader {
  /** The discriminator that separates this line from a frame. */
  kind: 'header'
  version: typeof LOG_SCHEMA_VERSION
  sessionId: string
  roomCode: string
  mapName: string
  /** ISO — when the round started. */
  startedAt: string
  players: Array<{ id: PlayerId; seat: number; name: string | null }>
  /**
   * The tick the audible anchor was emitted on, observed on the tape rather
   * than promised in advance: the header is written with the first flush, by
   * which time tick 0 has already gone past. `null` means no beep was seen,
   * which is the same thing the manifest says about the same file.
   */
  syncBeepTick: number | null
  /** The content as it was when this round was played. Never re-fetched. */
  catalogue: ClientConfig
  /**
   * Every tuning knob this round was played under, verbatim from
   * `timings.json`.
   *
   * The catalogue deliberately carries no clocks — the browser renders absolute
   * tick stamps and never looks a duration up, which is what keeps a mis-tuned
   * client from drawing a lying bar. That property is worth keeping, and it
   * leaves the log unable to say which condition it is. This block says it, on
   * the log only: an analyst reads the numbers out of line 1 without a git
   * checkout, and the client still learns nothing.
   */
  timings: LoggedTimings
  /**
   * A sha256 over the timings, the recipes and the map this round used.
   *
   * Beside the values rather than instead of them: a hash proves two sessions
   * ran the same condition and cannot be read, while the values can be read and
   * cannot prove anything about the map or the recipe DAG.
   */
  contentHash: string
  /**
   * Which condition this round was — the field Evan's thirty trajectories are
   * sorted by. Version 3; a version-2 file does not carry it, and a reader that
   * wants to bucket one has only the seats to go on.
   */
  condition: LogCondition
  /**
   * Who was in each seat, and what was driving it.
   *
   * Beside `players` rather than instead of it: `players` is the roster the
   * round was played with and every version of this header has carried it, so
   * folding a kind into it would break every reader that already parses one.
   */
  participants: SessionParticipant[]
  /**
   * The invite code the room was opened under — the intake batch this round
   * belongs to. Version 4.
   *
   * Written as `null`, never left out, when the room had no code (a server
   * started with `REQUIRE_INVITE=0`), so a version-4 reader can tell "no batch"
   * from "a file too old to say". Optional in the type because a version-1 to
   * version-3 file does not carry it, and a reader must treat that as `null`.
   * The code is the room's: a partner who joined through the link played under
   * the code the creator typed.
   */
  invite?: string | null
  /**
   * One entry per seat, as it stood at the whistle: every player has recorded
   * nothing yet, so these are pointers, not results. What actually landed is
   * the manifest's business — it is written afterwards and can therefore say.
   *
   * An agent seat is here too, and its pointer stays `missing` for the whole
   * round, because there is no microphone behind it. `participants` above is
   * what tells a reader the difference between that and a recording that was
   * lost.
   */
  audio: AudioArtifact[]
  /**
   * What the round cost, per model — the fourth thing line 1 has to say.
   *
   * Optional, and it is the one field on this line that is not written when the
   * line is. The header rides out with the trace's first flush, by which time the
   * round has cost nothing yet; the bill is known at the whistle, so
   * `finaliseSession` rewrites line 1 once the seats have reported. A round with
   * no models in it never grows one, and neither does a version-3 log recorded
   * before the field existed — both parse, and both are correct about having
   * cost nothing that anybody measured.
   */
  cost?: RoundCost
  /**
   * What the round amounted to — version 6. Amended onto line 1 at the whistle,
   * exactly as `cost` is, so `head -1 log.jsonl` says how the round went as
   * well as what it was. Absent on a round that never reached its whistle
   * cleanly and on every log older than version 6.
   */
  summary?: RoundSummary
  /**
   * Which game of which study dyad this round was, for a round played in study
   * mode, and absent otherwise. Optional and additive, so the schema version
   * does not move: every older reader ignores it and every older log lacks it.
   * Internal ids only — the Prolific id stays in the admin-only study store.
   */
  study?: SessionStudyBlock
  /**
   * Every setting the oracle solved this round under — present exactly when
   * `condition` is `'oracle'`. Additive, so the schema version does not move.
   */
  oracle?: OracleSettings
}

/** A parsed `log.jsonl`: at most one header, and the frames. */
export interface SessionLog {
  /** `null` for a version-1 file, recorded before the header existed. */
  header: SessionLogHeader | null
  frames: EventFrame[]
}

// ---------------------------------------------------------------------------
// Socket contract
// ---------------------------------------------------------------------------

export interface ClientToServerEvents extends StudyClientEvents {
  /**
   * `map` is honoured only for the player who creates the room — a map is a
   * property of the room, not of a seat, so the second arrival inherits it.
   */
  /**
   * `kind` and `model` are how a seat says it is not a person.
   *
   * Optional, so every browser build that predates them still joins and is
   * seated as a human — the default a browser could not be anything else. They
   * change nothing about the round: an agent seat gets the same `PlayerId`, the
   * same frames and the same input path, and the only consequence is that the
   * log header can say which condition the round was and that a seat with no
   * microphone is expected to be silent rather than broken.
   */
  joinRoom: (payload: {
    roomCode: string
    playerName?: string
    map?: string
    kind?: 'human' | 'agent'
    model?: string
    /**
     * Required with `kind: 'agent'`: `<roomCode>.<expiresAt>.<hmac>`, minted by
     * the server's launcher or from `AGENT_TOKEN_SECRET`. Without a valid one
     * the join is refused with `AGENT_TOKEN_REFUSED`.
     */
    agentToken?: string
    /**
     * `false` from an agent seat that will not speak this round — the no-talk
     * arm of the talk vs no-talk comparison. Recorded on the header's
     * participant; the seat's utterances are not put on the tape. Absent means
     * the seat may talk. Ignored from a person, whose voice is their own.
     */
    talk?: boolean
    /**
     * The player's agreement to be recorded. Required of a person joining a
     * room with more than one seat; the join is refused with
     * `CONSENT_REFUSED` without a record for the current `CONSENT_VERSION`.
     */
    consent?: ConsentRecord
  }) => void
  /**
   * "I am seated, I can hear, start when the others say so." The round timer is
   * finite from phase 5 onward, so it must not start while somebody is still
   * negotiating voice or staring at a lobby. Sent again after `ROUND_ENDED` to
   * ask for another round in the same room.
   */
  ready: () => void
  /**
   * "I am through the gates." Sent once the last setup screen clears, which is
   * a different moment from `ready` — `ready` is consent to start, this is only
   * "there is a person here with their hands on the keys".
   *
   * The server holds a *forced* round start while a seat is still in setup, so
   * that a player who is still reading is not dropped into a running round with
   * no key listeners attached. That was the likeliest cause of "the controls do
   * not work" that was not the extinguisher.
   */
  setupDone: () => void
  input: (payload: PlayerAction) => void
  latencyPing: (payload: { sentAt: number }) => void
  rttSample: (payload: { rttMs: number }) => void
  voiceSignal: (payload: { to: PlayerId; signal: RtcSignal }) => void
  /** Ask for a fresh mint — an ICE restart needs credentials that have not expired. */
  requestVoiceCredentials: () => void
  /**
   * One `MediaRecorder` timeslice, streamed while the round runs.
   *
   * `seq` is monotonic from 0 and is what the server sorts and gap-checks by —
   * `Blob.arrayBuffer()` can resolve out of order, and a WebM whose EBML header
   * lands mid-file is a file no decoder will open. Craft Together lost roughly
   * half its recordings to exactly that before the seq was honoured.
   *
   * The chunk is never keyed by a seat number. The server derives the player
   * from the socket, so the blob is `player_{playerId}.webm` and a third seat
   * needs no new branch.
   */
  audioChunk: (payload: {
    chunk: ArrayBuffer
    seq: number
    mimeType?: string
    /**
     * Which round's recording this belongs to.
     *
     * The tail of a recording is produced *after* `ROUND_ENDED`: the final
     * `ondataavailable` fires on `recorder.stop()`, which the client only calls
     * once it has been told the round is over, and the blob then goes through an
     * `await blob.arrayBuffer()` on top of that. The server therefore has to be
     * willing to accept chunks for a short window past the whistle — and the
     * moment it is, "which recording" stops being answerable from the room,
     * because the room may already have been reset for another go. The client
     * says which, and a tail whose session does not match the one closing is
     * dropped rather than spliced into the next round's audio.
     *
     * Optional so an older client is still ingested, exactly as before.
     */
    sessionId?: string
    /**
     * This recorder run's id. A page reloaded mid-round starts a new recorder,
     * whose seq restarts at 0 and whose first chunk is a new container header;
     * the id is what keeps the two runs in separate files instead of spliced
     * into one. Absent from an older client, which is one run.
     */
    recordingId?: string
  }, ack?: (result: AudioChunkAck) => void) => void
  /**
   * "That is all of it." Carries the client's own accounting so the server can
   * say whether what it received is what was sent, rather than assuming.
   */
  audioDone: (payload: {
    lastSeq: number
    chunkCount: number
    mimeType?: string
    /** Which round is being accounted for. See `audioChunk`. */
    sessionId?: string
    /** Which recorder run is being accounted for. See `audioChunk`. */
    recordingId?: string
    /** Whether the recorded stream carried the sync beep. */
    syncBeep?: boolean
  }) => void
  /**
   * "My voice link is now in this state." Logged as `VOICE_STATE` during a
   * round; ignored outside one.
   */
  voiceState: (payload: {
    state: 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed'
    relayed?: boolean
  }) => void
  /**
   * "I was already here." The reload path.
   *
   * A dropped socket leaves its seat reserved for `RESUME_GRACE_MS`; this is
   * how the returning tab claims it back. `resumeToken` is the proof — an HMAC
   * over `(roomCode, playerId, expiry)`, minted with `joined` — because the
   * `playerId` alone is a name anybody who saw a `roomState` could say.
   *
   * It is deliberately not `joinRoom` with an extra field: a rejoin that fails
   * must not seat the caller as somebody new, and a join that succeeds must not
   * quietly resurrect a seat. The server answers a bad rejoin with
   * `errorMessage` and the client falls back to a fresh `joinRoom`.
   */
  rejoin: (payload: { roomCode: string; playerId: PlayerId; resumeToken: string }) => void
  /**
   * "I was here in a tab I closed." The reopened-tab path.
   *
   * The same claim as `rejoin`, with one difference that makes it safe to make
   * from a claim this browser remembers rather than one this tab does: it only
   * takes a seat nobody is sitting in. `rejoin` hands a connected seat over to
   * the new socket, which is right for a reload but would let a second tab in
   * the same browser — two testers on one laptop — pull its partner out of
   * their own chair. A seat that is connected is refused with `errorMessage`,
   * and the client walks in the front door like anybody else.
   */
  reclaim: (payload: { roomCode: string; playerId: PlayerId; resumeToken: string }) => void
  /**
   * "I said this."
   *
   * The text channel a seat with no microphone talks on. The server stamps it
   * with the current tick and puts it on the tape as an `UTTERANCE`, which is
   * both how the other seat hears it and how the replay page reads it back.
   *
   * Deliberately not a chat relay: there is no second delivery path and no
   * per-socket shaping, so what one agent said and what the log recorded are
   * the same bytes for the same reason every frame is (`emit()` has one body).
   */
  utterance: (payload: { text: string; channel?: 'text' | 'tts'; latencyMs?: number }) => void
  /**
   * "I decided to do this, and here is what deciding cost."
   *
   * Recorded, never simulated: the server does not read the macro and could not
   * act on it if it wanted to — the seat still sends the same move and interact
   * edges. It is on the wire so it can be on the tape.
   */
  plan: (payload: {
    macro: Macro
    reason?: string
    /** The coalesced re-plan triggers this decision answered. See `PLAN`. */
    answering?: string[]
    model?: string
    latencyMs?: number
    promptTokens?: number
    completionTokens?: number
  }) => void
  /**
   * "This is what my thinking cost."
   *
   * Sent once, at the end of a round, by a seat that paid for any. The tokens
   * and the dollars are the provider's own figures and only the seat that made
   * the calls has them — the server never speaks to a model and has no way to
   * reconstruct a price it was not told.
   *
   * It is not the sum of the round's `PLAN` events, and the difference is the
   * point: a call that came back unreadable left no `PLAN` on the tape and still
   * cost money, and cached prompt tokens are a fact about the provider's
   * accounting rather than about any one decision. This is the invoice; the
   * `PLAN` events are the decisions.
   *
   * The server stamps the seat and the model from the room rather than from the
   * payload, so a header that names a model and a tape that names another cannot
   * disagree.
   */
  cost: (payload: {
    calls: number
    parseFailures?: number
    callFailures?: number
    promptTokens: number
    cachedPromptTokens?: number
    /** Whether the provider reported a cache split at all. See `SeatCost`. */
    cacheReported?: boolean
    completionTokens: number
    costUsd?: number
    thinkingMs?: number
    ceilingHit?: boolean
  }) => void
}

export interface ServerToClientEvents extends StudyServerEvents {
  /**
   * `sessionToken` is an HMAC over this socket's id. It gates only the paid
   * HTTP surface (`GET /api/ice-servers`); gameplay events are gated by having
   * a seat, which the socket already proves.
   */
  joined: (payload: {
    playerId: PlayerId
    roomCode: string
    sessionToken: string
    /** The blob key prefix for this round — shown on the end screen. */
    sessionId: string
    /**
     * The seat, in a form a returning tab can present. An HMAC over
     * `(roomCode, playerId, expiry)` — bound to the seat rather than to the
     * socket, because the socket id is precisely what a reload changes.
     *
     * Persist it and send it back as `rejoin`. It is not a second
     * `sessionToken`: that one gates the paid HTTP endpoint for *this* socket
     * and dies with it.
     */
    resumeToken: string
    /**
     * The fewest milliseconds between two moves the server accepts from an
     * agent seat (`timings.json` `agentMoveIntervalMs`). An agent paces itself
     * to it; a browser ignores it, because human seats are never paced.
     * Optional so a server that predates it can still be joined.
     */
    agentMoveIntervalMs?: number
  }) => void
  frame: (payload: EventFrame) => void
  latencyPong: (payload: { sentAt: number }) => void
  /**
   * An input the transport threw away, told to the socket that sent it.
   *
   * Transport-level, never a `GameEvent`: it is not part of the round and must
   * not reach the event tape, which is what `determinism.test.ts` folds.
   */
  inputRejected: (payload: { reason: InputRejectionReason }) => void
  roomState: (payload: {
    players: Array<{
      id: PlayerId
      seat: number
      ready: boolean
      /** Still working through the gates. The lobby can say what the other chef is doing. */
      inSetup: boolean
      /**
       * Whether a socket is currently attached to this seat.
       *
       * False for the length of the resume grace window, which is the whole
       * point of saying it: "seat 2 dropped, waiting for them" is a different
       * sentence from "seat 2 is empty", and only one of them is worth waiting
       * through.
       */
      connected: boolean
      /**
       * What is behind this seat, as the seat declared on `joinRoom`.
       *
       * The roster is the only place a browser can learn it, and there are
       * exactly two things it changes on the human's side of a human-AI round.
       * A seat with no microphone has no WebRTC to negotiate, so the client must
       * not open a peer connection to it and must not hold the round timer
       * waiting for one to connect — six seconds of a finite round spent
       * negotiating with a program. And a seat with no mouth speaks in
       * `UTTERANCE` events, which is what the browser reads aloud.
       *
       * Optional so a server that predates it can still be talked to by a
       * current bundle; absent reads as `human`, the only thing a browser could
       * have been.
       */
      kind?: 'human' | 'agent'
      /** The model driving an agent seat, for the lobby to name. */
      model?: string | null
    }>
    phase: RoomPhase
    /** Re-issued when a round ends and the room is reset for another go. */
    sessionId: string
    /** Which preset this room plays on. Fixed by whoever opened the link. */
    mapName: string
  }) => void
  voiceSignal: (payload: { from: PlayerId; signal: RtcSignal }) => void
  voiceCredentials: (payload: IceConfig) => void
  errorMessage: (payload: string) => void
  /**
   * This room is gone, and here is why. Terminal: the client shows the closed
   * screen, forgets any stored seat, and does not try to join again.
   *
   * Sent to a `joinRoom` for a code the server does not hold (`not-found`),
   * and to everybody in a room the server is closing on its own.
   */
  roomClosed: (payload: RoomClosedPayload) => void
}

/**
 * Why a room stopped existing, as far as the people in it are told.
 *
 * - `nobody-joined`: a lobby waited too long for a partner.
 * - `abandoned`: a lobby nobody was connected to sat idle.
 * - `idle`: an ended round nobody played again.
 * - `not-found`: the code named no room — a stale link or a reload after the
 *   room was deleted. Rooms are created only by `POST /api/rooms`, never by a join.
 */
export type RoomClosedReason = 'nobody-joined' | 'abandoned' | 'idle' | 'not-found'

export interface RoomClosedPayload {
  roomCode: string
  reason: RoomClosedReason
}

export type RoomPhase = 'lobby' | 'playing' | 'ended'

// ---------------------------------------------------------------------------
// The oracle
//
// `@overcooked/oracle` plans both chefs of a kitchen over the real engine and
// reports the best round it found. These are the wire fields of that result:
// the CLI's `--json` output and, later, the server's payload, so the two can
// never describe one solve in different words.
// ---------------------------------------------------------------------------

/**
 * How much of the order schedule the oracle's planner reads ahead of time,
 * which names the role a solve plays.
 *
 *   - `'full'`: **the Oracle**. Both chefs planned together, knowing every
 *     order the kitchen will issue. The default, and what every surface shows.
 *   - `0`: **the baseline**. Only the tickets on the rail (`state.orders`),
 *     what a team sees the first time it plays a map.
 *
 * A log written before the field existed planned with `'full'`.
 */
export type OracleLookahead = 0 | 'full'

/**
 * The two solves the server keeps for every kitchen, by the role each plays:
 * `oracle` (lookahead `'full'`) and `baseline` (lookahead `0`).
 */
export type OraclePlan = 'oracle' | 'baseline'

/** The lookahead each plan solves with. */
export const ORACLE_PLAN_LOOKAHEAD: Readonly<Record<OraclePlan, OracleLookahead>> = { oracle: 'full', baseline: 0 }

/**
 * Every setting a solve ran under. A score is only comparable with another
 * score solved under the same settings, so every result carries all of them.
 */
export interface OracleSettings {
  /** Ticks between two moves of one chef — the oracle's walking pace. */
  stepTicks: number
  /** The same pace in milliseconds. */
  stepMs: number
  /** Where the pace came from: the content's `oracleStepMs`, or a CLI override. */
  stepSource: 'timings.oracleStepMs' | 'cli'
  /** The reproducible search limit: nodes expanded. Wall-clock is never the limit. */
  maxExpansions: number
  beamWidth: number
  /** A safety abort only. A solve that hits it is `truncated`. */
  budgetMs: number
  /** Idle ticks before each macro (0: the oracle reacts instantly). */
  reactionTicks: number
  /** Solve with cross-chef counter pickups forbidden (an ablation). */
  forbidHandoffs: boolean
  /** How far ahead the planner reads the order schedule: `'full'`, every order (the Oracle); 0, only the tickets on the rail (the baseline). */
  lookahead: OracleLookahead
  /** `contentHash(content, map)` of the content solved. */
  contentHash: string
  solverVersion: string
  /** The frozen difficulty reference the result was scored against, if any. */
  referenceVersion: string | null
}

/** One chef's side of the controller's account of a round. */
export interface OraclePlayerTrace {
  moves: number
  presses: number
  holds: number
  /** Macros started (go somewhere, face a station, press or hold). */
  macros: number
  /** Times this chef stepped aside for the other. */
  yields: number
  /** The longest run of steps this chef wanted to move and could not. */
  maxStuckSteps: number
  /**
   * The longest run of steps this chef stood holding an item it could not put
   * anywhere because a station or counter it needed was occupied — a wait on
   * somebody, never on a cook clock.
   */
  maxResourceWaitSteps: number
}

/**
 * How the oracle's controller kept two chefs moving: every conflict it met and
 * how each was settled. Counts, so they can be compared across maps.
 */
export interface OracleTrace {
  players: Record<PlayerId, OraclePlayerTrace>
  /** Head-on wait-for cycles between two chefs' paths. */
  cycles: number
  /** Cycles a joint BFS over both positions resolved. */
  jointPlans: number
  /** Cycles nothing better than one chef stepping aside resolved. */
  yieldFallbacks: number
  /** Of those, the ones where no better cell existed and the chef backed off along its own path. */
  retreats: number
  /** Macros dropped unfinished: blocked too long, or past their timeout. */
  aborts: number
  /** Of the aborts, the ones a macro timeout ended (2 × estimate + 4 steps). */
  timeouts: number
  /** Holds ended by a collision, a contest or the item being taken; the progress banks and the chef replans. */
  interrupts: number
  /** Occupied counters or boards a free chef cleared because the other was waiting on them. */
  cleared: number
  /** Resource deadlocks (both chefs holding, or one holding with nobody to clear) broken by parking or binning. */
  resourceCycles: number
}

/**
 * One term of the difficulty composite: what it measured on this round, where
 * the frozen reference set sits on it, and how much it weighs.
 *
 * Everything needed to recompute the number is here — `z` is
 * `(value − mean) / sd`, and `difficulty` is the weighted mean of the `z`s
 * over the terms with `sd` above 0 (a term the reference does not vary on has
 * `z` 0 and weight 0) — so a reader never has to trust it.
 */
export interface DifficultyTerm {
  /** Stable, for code and tests: `shortfall`, `actionsPerDish`, `crossingsPerDish`, … */
  id: string
  /** How a person reads it: "1 − score ratio", "crossings / dish", … */
  label: string
  value: number
  mean: number
  sd: number
  z: number
  weight: number
}

/** The published basis of a difficulty number. */
export interface DifficultyBasis {
  /** The frozen reference set the z-scores are taken against (`reference.json`). */
  referenceVersion: string
  /** The maps that set is made of. */
  referenceMaps: string[]
  terms: DifficultyTerm[]
}

/**
 * The oracle's difficulty proxies for one verified round.
 *
 * Event-derived wherever the engine's events say it — so the same numbers can
 * later be read off a human or an AI round — and from the controller's own
 * account where only the controller knows (whether a chef stood still by
 * choice or because the other was in the way). Time is counted in **steps**:
 * one step is `stepTicks`, the oracle's walking pace.
 */
export interface OracleMetrics {
  score: number
  servedByDish: Record<string, number>
  expired: number
  /** How many different dishes were served (matched) at least once. */
  distinctDishesServed: number
  /** Matched serves of dishes with one cooked item, and with two or more. */
  simpleServed: number
  complexServed: number

  /** `PLAYER_MOVED` per chef. */
  steps: Record<PlayerId, number>
  /** Moves + interact key presses + completed holds (a chop, a wash, a fire put out), per chef. */
  actions: Record<PlayerId, number>
  /** Steps with no macro in progress — nothing to do, or a wait the planner chose (a pan still cooking). */
  idleSteps: Record<PlayerId, number>
  /**
   * Steps a chef stood still while its partner stood on every next cell of its
   * shortest ways to the station it used next: the waits within `interference`.
   * Read off the paths alone, so a human or an AI round has it too.
   */
  waitForPartnerSteps: Record<PlayerId, number>
  /**
   * Steps a chef stood holding something for a full counter, board, pan or
   * sink. From the controller's account, so only an oracle round has it.
   */
  waitForStationSteps: Record<PlayerId, number>
  /** `|a₁ − a₂| / (a₁ + a₂)` over actions; 0 when nobody acted, and 0 in a solo kitchen. */
  imbalance: number

  /** Constructive hand-offs: counter + station, whose item ended up in a matched serve. */
  handoffs: number
  /** Constructive hand-offs over a counter: one chef puts it down, the other picks it up. */
  counterHandoffs: number
  /** Constructive hand-offs at a station: one chef puts it on a board or in a pan, the other does the next step there. */
  stationHandoffs: number
  /** Hand-offs (either kind) whose item never reached a matched serve: binned, burnt, or still about at the whistle. */
  wastedHandoffs: number
  /** A receiver standing at an empty hand-off counter for it, plus a giver held by a full one. */
  handoffWaits: number
  /** Static: the fewest times each dish's items must cross between chefs, from the layout alone. */
  forcedHandoffsPerDish: Record<string, number>

  /**
   * Steps, both chefs together, in which a chef ended off every shortest path
   * to the station it used next while its partner stood on one: it waited for
   * the partner, or went round. From the paths alone (static BFS), so it reads
   * the same off a human, an AI or an oracle round.
   */
  interference: number
  /** Times a chef stepped into a cell the other stood on within the last 2 steps; a swap or a pass-through counts once. */
  crossings: number
  /**
   * How far each kind of job (fetch, chop, cook, plate, serve, wash, pass) is
   * one chef's: 0 when every kind is split evenly, 1 when each is done by one
   * chef alone. `null` in a solo kitchen.
   */
  specialization: number | null
  /** The share of the round's steps with two or more dishes in progress (first ingredient fetched, not yet served). */
  overlap: number
  /** Per dish served (or per round, when nothing was): */
  perDish: { actions: number; handoffs: number; interference: number; crossings: number }
  /** Idle steps over every chef's steps in the round. */
  idleShare: number

  /** The best hand-written policy's score: the floor the search starts from. */
  portfolioScore: number
  /** `score − portfolioScore`. */
  searchGain: number
  /** The analytic ceiling: no round on this kitchen can score more. */
  ceiling: number
  /** `ceiling − score`. */
  ceilingGap: number
  /** `score / ceiling`, clamped to 0…1; 0 when the ceiling is not above zero. */
  scoreRatio: number

  /** The composite over the frozen reference set. `null` for a truncated result, which is never compared. */
  difficulty: number | null
  difficultyBasis: DifficultyBasis
}

/** What a solve found, in the shape every surface reports it. */
export interface OracleResultCore {
  map: string
  status: 'ok' | 'impossible'
  reason?: string
  /** The engine's final score for the verified tape. Can be negative. */
  score: number
  /** Matched serves by the dish the ticket asked for. */
  servedByDish: Record<string, number>
  /** Tickets that lapsed. Tickets still open at the whistle cost nothing. */
  expired: number
  /** The winning portfolio policy, by name (`idle` when nothing beat standing still). */
  policy: string
  /** Moves in the winning tape, both chefs together — the first tie-break after score. */
  steps: number
  /** sha256 of the winning tape, so two solves can be compared without shipping either tape. */
  tapeHash: string
  /** Search nodes the beam expanded (absent on results solved before the beam). */
  expansions?: number
  /** The search spent `maxExpansions` before running out of work. */
  expansionsExhausted: boolean
  /** The wall-clock safety abort fired. A truncated result is never a reference. */
  truncated: boolean
  elapsedMs: number
  settings: OracleSettings
  /** The controller's account of the winning round (all zeros for the idle tape). */
  trace: OracleTrace
  /** The difficulty proxies, the ceiling and the composite. */
  metrics: OracleMetrics
  /** The same kitchen re-solved under ablations (`npm run oracle -- run --ablations`); absent otherwise. */
  ablations?: OracleAblations
  /**
   * How the round stands against the best possible score, and whether every
   * order was served. Absent on results solved before it existed.
   */
  verdict?: OracleVerdict
}

/**
 * The replayed round read against the analytic ceiling (the best possible
 * score). Everything here is counted from the verified round's events, except
 * the ceiling, which is arithmetic over the map and errs high.
 */
export interface OracleVerdict {
  /** `score === ceiling`: no round on this kitchen can score more. */
  provedBest: boolean
  /** `ceiling − score`. Never below 0 while the ceiling is sound. */
  gap: number
  /** Tickets that reached the rail in the round. A ticket a full rail turned away never reached it. */
  ordersIssued: number
  /** Of those, how many a serve matched. */
  ordersServed: number
  /**
   * Tickets the kitchen would have issued in the round had the rail never
   * filled (its schedule, else one per cadence slot). Absent on results solved
   * before it existed.
   */
  ordersExpected?: number
  /**
   * Of `ordersExpected`, how many never reached the rail: a full rail turned
   * them away, and the cadence passed the slot without an event. Absent on
   * results solved before it existed.
   */
  ordersSkipped?: number
  /** Every expected ticket reached the rail and was served: nothing skipped, `ordersServed === ordersIssued`. */
  allServed: boolean
  /** The tick of the last matched serve; null when nothing was served. */
  lastServeTick: number | null
  /** The longest a served ticket waited, from arriving on the rail to served, in ticks; null when nothing was served. */
  worstWaitTicks: number | null
}

/**
 * The kitchen re-solved with one thing taken away, at the same settings as
 * the result it belongs to. Each says what the kitchen asks of
 * a team that the plain score does not.
 */
export interface OracleAblations {
  /**
   * Solved with cross-chef counter pickups forbidden: nothing handed over.
   * `required` when the kitchen then serves no dish at all although it serves
   * some with hand-offs — no round avoids passing.
   */
  requiredHandoffs: {
    required: boolean
    /** Score and matched serves without hand-offs. */
    scoreWithout: number
    servedWithout: number
    /** `score − scoreWithout`. */
    drop: number
  }
  /**
   * `score ÷` the best single-chef score (one chef works, the other only
   * steps aside). `null` when no single chef scores above zero — the team is
   * the only way to serve — or the kitchen has one seat.
   */
  teamAdvantage: number | null
  /** Each seat working alone: its best score. Empty in a one-seat kitchen. */
  soloScores: Record<PlayerId, number>
  /** A human-latency variant: every macro starts `reactionTicks` late. */
  reaction: {
    reactionTicks: number
    reactionMs: number
    /** The score with that delay, and with none. */
    score: number
    instantScore: number
    /** `instantScore − score`. */
    drop: number
  }
}

/**
 * Where a map's oracle result stands, as the server reports it.
 *
 *   - `ready`: solved at the full budget and cached; the summary and the log
 *     can be read.
 *   - `solving`: not in the cache yet — queued, running, or waiting for the
 *     live rounds to finish. Reads never start a solve.
 *   - `failed`: the solve crashed or timed out on every attempt; it is not
 *     retried for the same content and settings.
 *   - `impossible`: the map provably cannot serve a dish.
 */
export type OracleStatus = 'ready' | 'solving' | 'failed' | 'impossible'

/** One row of `GET /api/oracle`: enough for a badge, nothing that needs the log. */
export interface OracleListEntry {
  map: string
  status: OracleStatus
  score?: number
  truncated?: boolean
  /** Which budget the number was found under: "full search (N expansions)". */
  budgetLabel: string
}

/** `GET /api/oracle/:map` once the map is no longer `solving`. */
export interface OracleSummary {
  map: string
  status: Exclude<OracleStatus, 'solving'>
  budgetLabel: string
  /**
   * `contentHash(content, map)` of the content the server is serving *now*.
   * The log header carries the hash it was solved under; a watch page that
   * finds the two different says the kitchen has changed since.
   */
  contentHash: string
  /** The solve, for `ready` and `impossible`; `null` when it failed. */
  result: OracleResultCore | null
  /** Why a `failed` solve failed, and after how many attempts. */
  failure?: { attempts: number; error: string }
  /**
   * The baseline's solve of the same kitchen (first play, the tickets on the
   * rail only), joined on read: where it stands and, once solved, its score.
   * No metrics and no difficulty: difficulty is the Oracle's alone. Absent
   * from an editor's draft check, which solves the Oracle only, and from
   * summaries served before the baseline existed.
   */
  baseline?: OracleBaselineSummary
}

/** The baseline beside an Oracle summary: its status, and its score once solved. */
export interface OracleBaselineSummary {
  status: OracleStatus
  score?: number
  truncated?: boolean
}

// ---------------------------------------------------------------------------
// The event fold, and the derived reads that go with it.
//
// Re-exported last so this module stays the single import for both packages.
// `fold.ts` imports only types from here, so there is no runtime cycle.
// ---------------------------------------------------------------------------

export {
  applyToSnapshot,
  carryableContents,
  containerOnStation,
  findContainer,
  heldProgressFraction,
  heldProgressTicks,
  phaseFraction,
} from './fold'

export { PINNED_MODEL } from './models'

// Prolific study mode. Additive: nothing above reads it.
export * from './study'

// Round summaries and data-quality verdicts. Type-only on this file, so there
// is no runtime cycle.
export * from './quality'
